import { KeyboardBackspace } from "@mui/icons-material";
import { Container, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetPatientPreferredPharmacyQuery } from "src/api/services/preferredPharmacyApi";
import EditPencilIcon from "src/assets/icons/EditPencilIcon";
import { useIsMobile } from "src/hooks/useIsMobile";
import WhiteCard from "src/shared/components/WhiteCard";
import UpdatePreferredPharmacyModal from "./components/UpdatePreferredPharmacyModal";

const PreferredPharmacyInformation = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data, isLoading } = useGetPatientPreferredPharmacyQuery();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const page = "preferred-pharmacy-information";

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  if (isLoading) {
    return <Typography>Loading preferred pharmacy information...</Typography>;
  }

  if (data == null) {
    return;
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <Stack gap={3}>
        <Stack direction="row" alignItems="center">
          {isMobile ? (
            <IconButton sx={{ color: "#00B6B8" }} onClick={() => navigate(-1)}>
              <KeyboardBackspace fontSize="large" />
            </IconButton>
          ) : null}
          <Typography variant="h5" fontWeight={700}>
            {t(`${page}.header`, "Preferred Pharmacy")}
          </Typography>
        </Stack>
        <WhiteCard>
          <Stack direction={"row"} gap={3}>
            <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
              {t(`${page}.title`, "Pharmacy Address")}
            </Typography>
            <Stack
              direction={"row"}
              alignItems="center"
              sx={{ gap: 0.5, cursor: "pointer" }}
              onClick={() => setOpenModal(true)}
            >
              <EditPencilIcon />
              <Typography sx={{ color: "#00B6B9" }}>
                {t(`account.overview.edit`)}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="flex-start"
            gap={isMobile ? 2 : 5}
          >
            <Stack gap={0.5} maxWidth={"400px"}>
              <Typography variant="subtitle1">
                {t(`${page}.pharmacy.name`, "Name")}
              </Typography>
              <Typography>{data.name}</Typography>
            </Stack>
            <Stack gap={0.5} maxWidth={"400px"}>
              <Typography variant="subtitle1">
                {t(`${page}.pharmacy.full_address`, "Full address")}
              </Typography>
              <Typography>{data.full_address}</Typography>
            </Stack>
          </Stack>
        </WhiteCard>
      </Stack>
      <UpdatePreferredPharmacyModal
        onClose={handleClose}
        open={openModal}
        patientPharmacy={data}
      />
    </Container>
  );
};

export default PreferredPharmacyInformation;
